/**
 * AddPolicyPartyModal — Task #368 (2026-06-09, §3g Phase 1).
 *
 * Modal-that-stages: the reusable shape for "rich" relationship-add flows
 * (4+ fields) under §3g Pattern A. Unlike the legacy
 * /insurance-policy-parties/new standalone route — which committed the
 * CREATE immediately on Save — this modal returns the staged payload to
 * the parent via `onStage(payload)`. The parent appends it to its
 * pendingOps queue and commits the whole batch through maker-checker
 * when the user clicks Save Changes on the page.
 *
 * Pairs with NamedRMSection (the "sparse" inline-picker pattern, Task
 * #326). Both stage to the same parent pendingOps queue; only the input
 * chrome differs based on field count.
 *
 * Mirrors the field structure of the standalone
 * InsurancePolicyPartyDetailPage form (Person picker, Role,
 * Designation/Share/Date when BENEFICIARY, Notes), so the standalone
 * route stays viable as a backstop (kept under §3g-Q2: "legacy-unused").
 *
 * P18 (team feedback fb 33, 2026-10-10): opens on the same Create new person /
 * Attach existing person chooser as the Agreement's Add-party modal
 * (PartyAddSteps), including the create-person round-trip resume.
 */
import {
  Alert, Button, Group, Modal, NumberInput, Select, Stack, Text, Textarea, TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { PersonPicker } from './EntityPicker';
import {
  PartyAddChooser, PartyCreatePersonStep, newPartyPersonUrl, usePartyAddStep,
} from './PartyAddSteps';
import { usePerson } from '../hooks/usePersons';
import type {
  BeneficiaryDesignation, InsurancePolicyPartyInput, InsurancePolicyPartyRole, UUID,
} from '../api/types';

const ROLE_OPTS: { value: InsurancePolicyPartyRole; label: string }[] = [
  { value: 'POLICY_HOLDER', label: 'Policy Holder' },
  { value: 'LIFE_INSURED', label: 'Life Insured' },
  { value: 'BENEFICIARY', label: 'Beneficiary' },
];

const DESIGNATION_OPTS: { value: BeneficiaryDesignation; label: string }[] = [
  { value: 'PRIMARY', label: 'Primary' },
  { value: 'CONTINGENT', label: 'Contingent' },
];

interface FormValues {
  person_id: string;
  role: InsurancePolicyPartyRole;
  designation: string;           // '' or BeneficiaryDesignation
  percentage_share: number | '';
  designation_date: string;      // ISO date or ''
  notes: string;
}

const EMPTY: FormValues = {
  person_id: '',
  role: 'BENEFICIARY',
  designation: 'PRIMARY',
  percentage_share: '',
  designation_date: '',
  notes: '',
};

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Policy this party will be added to. Displayed read-only at the top
   *  of the modal for confirmation; used to build the payload. */
  policyId: UUID;
  policyLabel: string;
  /** Called with the built payload + the resolved Person display name
   *  (for friendly rendering on the pending row before the row exists in
   *  the DB). The parent appends a CREATE op to its pendingOps queue
   *  and dismisses the modal. */
  onStage: (payload: InsurancePolicyPartyInput, personDisplayName: string | null) => void;
  /** P18: when set, open straight on the Attach step with this person
   *  pre-selected (the resume leg of the "Create new person" round-trip). */
  initialPersonId?: string | null;
}

export function AddPolicyPartyModal({
  opened, onClose, policyId, policyLabel, onStage, initialPersonId,
}: Props) {
  const form = useForm<FormValues>({
    initialValues: EMPTY,
    validate: {
      person_id: (v) => (v.length >= 32 ? null : 'Person required'),
      designation: (v, all) =>
        all.role === 'BENEFICIARY' && !v ? 'Required for beneficiaries' : null,
    },
  });

  // Clean slate on every open (dismiss = discard, §3g); a resumed create
  // round-trip opens on 'attach' with the new person pre-selected.
  const [step, setStep] = usePartyAddStep(
    opened, initialPersonId, (pid) => form.setValues({ ...EMPTY, person_id: pid }),
  );

  const navigate = useNavigate();
  function handleCreateNew() {
    onClose();
    navigate(newPartyPersonUrl(`/account-insurance/${policyId}?tab=parties`));
  }

  // Role-driven field gating: when role flips away from BENEFICIARY,
  // blank the beneficiary-only fields so the staged payload doesn't
  // carry stale values (DB CHECK constraint ipp_non_beneficiary_no_designation
  // would otherwise reject on commit).
  const role = form.values.role;
  React.useEffect(() => {
    if (role !== 'BENEFICIARY') {
      if (form.values.designation) form.setFieldValue('designation', '');
      if (form.values.percentage_share !== '') form.setFieldValue('percentage_share', '');
      if (form.values.designation_date) form.setFieldValue('designation_date', '');
    } else if (!form.values.designation) {
      form.setFieldValue('designation', 'PRIMARY');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Resolve the picked Person's display name so the pending row renders
  // "Andre Pereira Alves" rather than an 8-char UUID prefix. Disabled
  // until person_id has a real value to avoid an enabled+empty fetch.
  const personQ = usePerson(form.values.person_id || undefined);
  const personDisplayName = personQ.data?.display_name ?? null;

  function handleSave(values: FormValues) {
    const payload: InsurancePolicyPartyInput = {
      policy_id: policyId,
      person_id: values.person_id,
      role: values.role,
    };
    if (values.role === 'BENEFICIARY') {
      payload.designation = (values.designation || 'PRIMARY') as BeneficiaryDesignation;
      payload.percentage_share =
        values.percentage_share === '' ? null : values.percentage_share;
      payload.designation_date = values.designation_date || null;
    } else {
      payload.designation = null;
      payload.percentage_share = null;
      payload.designation_date = null;
    }
    if (values.notes) payload.notes = values.notes;
    onStage(payload, personDisplayName);
    onClose();
  }

  // Per app-wide convention (#101 won't-do — picker excludeIds): no
  // client-side filter against already-assigned Person IDs. Duplicates
  // are caught by the backend 409 on Save, and surfaced as a Failed
  // marker on the pending row + friendly retry on the parent page.

  // C-113 guard (2026-06-10, surfaced by A4 smoke Test 1 on the Agreement
  // page): without stopPropagation the inner submit also fires the host
  // page's Save form through React event propagation. Same fix as
  // InteractionFormModal (#157); applied to all staging modals.
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    form.onSubmit(handleSave)(e);
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Add party to this policy" size="lg">
      {step === 'choose' && (
        <PartyAddChooser recordLabel={policyLabel} recordNoun="policy"
          onPick={setStep} onCancel={onClose} />
      )}

      {step === 'create' && (
        <PartyCreatePersonStep onBack={() => setStep('choose')} onOpenForm={handleCreateNew} />
      )}

      {step === 'attach' && (
      <form onSubmit={handleFormSubmit}>
        <Stack>
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={500}>{policyLabel}</Text>
            <Text size="xs" c="dimmed">
              The new party row is staged and only commits when you click
              Save Changes on the policy page.
            </Text>
          </Alert>

          <PersonPicker
            label="Person"
            description="Pick the person to attach in the selected role."
            value={form.values.person_id || null}
            onChange={(v) => form.setFieldValue('person_id', v ?? '')}
            error={form.errors.person_id as string | undefined}
          />

          <Select
            label="Role"
            data={ROLE_OPTS}
            value={form.values.role}
            onChange={(v) => v && form.setFieldValue('role', v as InsurancePolicyPartyRole)}
            allowDeselect={false}
            required
          />

          {form.values.role === 'BENEFICIARY' && (
            <Stack gap="xs">
              <Group grow align="flex-start">
                <Select
                  label="Designation"
                  data={DESIGNATION_OPTS}
                  value={form.values.designation || null}
                  onChange={(v) => form.setFieldValue('designation', v ?? '')}
                  error={form.errors.designation as string | undefined}
                  required
                />
                <NumberInput
                  label="Percentage share"
                  description="Sums to 100% across PRIMARY beneficiaries on the policy."
                  value={form.values.percentage_share}
                  onChange={(v) =>
                    form.setFieldValue(
                      'percentage_share',
                      typeof v === 'number' ? v : '',
                    )
                  }
                  min={0}
                  max={100}
                  decimalScale={2}
                />
                <TextInput
                  label="Designation date"
                  type="date"
                  value={form.values.designation_date}
                  onChange={(e) =>
                    form.setFieldValue('designation_date', e.currentTarget.value)
                  }
                />
              </Group>
            </Stack>
          )}

          <Textarea
            label="Notes"
            placeholder="Optional"
            value={form.values.notes}
            onChange={(e) => form.setFieldValue('notes', e.currentTarget.value)}
            autosize
            minRows={2}
          />

          <Group justify="space-between" gap="xs">
            <Button variant="default" onClick={() => setStep('choose')}>Back</Button>
            <Button type="submit">Stage party</Button>
          </Group>
        </Stack>
      </form>
      )}
    </Modal>
  );
}
